import debounce from 'lodash/debounce'
import { hasLocalImagePreview, storedImageNames } from '@/business-logic/image-credit-notes'

// Keeps the slides' image credits in step with their images, whatever kind of image changed
// (question, option, background, content block, plugin) and however (picked, replaced,
// deleted, undone): watching the images covers every path, including ones added later,
// without a hook in each image panel.
//
// The active slide is watched fully: images it gains and loses. Other slides are scanned for
// images they gain only (an upload that lands after the presenter moved on), because their
// content blocks are not always loaded and a missing block must never remove a credit.

const WIKIMEDIA_FLAG_GETTER = 'featureToggles/canUseWikimediaCommonsImageSource'
// An image lands in steps (local preview, then the uploaded copy); report once it settled.
export const IMAGE_SETTLE_MS = 500
// Right after a switch the slide's images are still loading (content blocks, plugin data):
// they are its starting point, not new images.
export const SLIDE_LOAD_GRACE_MS = 3000
export const OTHER_SLIDES_SCAN_MS = 1000

const pluginDataOf = (state, slideId) => state.slideAttributes?.[state.presentation?.id]?.[slideId]

const slideImages = (state, slide) => {
  // Content v2 keeps its blocks in the slide's plugin data, not on the slide itself.
  const pluginData = pluginDataOf(state, slide.id)
  return {
    names: storedImageNames(slide, pluginData),
    // While an image is a local preview its stored copy is missing, which would look like a
    // removed image: such a slide is not settled yet.
    uploading: hasLocalImagePreview(slide, pluginData),
  }
}

const activeSlideSnapshot = (state, getters) => {
  if (!getters[WIKIMEDIA_FLAG_GETTER] || !state.slideActive?.id) return null
  return JSON.stringify({ slideId: state.slideActive.id, ...slideImages(state, state.slideActive) })
}

// A tab in the background is usually someone else's screen following a co-editor's change:
// it must not write notes for it. It catches up once visible again.
const isTabVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden'

const reportChange = (tracker, slideId, previousNames, names, { removals }) => {
  const addedNames = names.filter((name) => !previousNames.includes(name))
  const removedNames = removals ? previousNames.filter((name) => !names.includes(name)) : []
  if (!addedNames.length && !removedNames.length) return
  tracker.store.dispatch('syncSlideImageCreditsAction', {
    slideId, addedNames, removedNames, currentNames: names, previousNames,
  })
}

const settleActiveSlide = (tracker, snapshot) => {
  tracker.pendingSlideId = null
  const { slideId, names, uploading } = JSON.parse(snapshot)
  if (uploading || !isTabVisible()) return
  const previousNames = tracker.settledNames.get(slideId)
  tracker.settledNames.set(slideId, names)
  const stillLoading = slideId === tracker.activeSlideId && Date.now() - tracker.switchedAt < SLIDE_LOAD_GRACE_MS
  if (!previousNames || stillLoading) return
  reportChange(tracker, slideId, previousNames, names, { removals: true })
}

const scanOtherSlide = (tracker, slide) => {
  const { names, uploading } = slideImages(tracker.store.state, slide)
  if (uploading) return
  const previousNames = tracker.settledNames.get(slide.id)
  // Never forget an image here: a content block that is not loaded is not a removed image.
  tracker.settledNames.set(slide.id, [...new Set([...(previousNames || []), ...names])].sort())
  if (previousNames) reportChange(tracker, slide.id, previousNames, names, { removals: false })
}

const scanOtherSlides = (tracker) => {
  const { state, getters } = tracker.store
  if (!getters[WIKIMEDIA_FLAG_GETTER] || !isTabVisible()) return
  const otherSlides = state.presentation?.slides || []
  otherSlides
    .filter((slide) => slide?.id && slide.id !== state.slideActive?.id)
    .forEach((slide) => scanOtherSlide(tracker, slide))
}

// Leaving a slide while its image uploads: this browser keeps the left slide's local preview,
// so the upload landing is never seen here. Its pending pick is credited instead.
const creditPickIfLeftUploading = (tracker, slideId) => {
  if (!tracker.latestSnapshot) return
  const left = JSON.parse(tracker.latestSnapshot)
  if (left.slideId !== slideId && left.uploading) {
    tracker.store.dispatch('creditPendingPicksAction', { slideId: left.slideId })
  }
}

const onActiveSlideImages = (tracker, snapshot) => {
  if (!snapshot) return
  const { slideId } = JSON.parse(snapshot)
  creditPickIfLeftUploading(tracker, slideId)
  tracker.latestSnapshot = snapshot
  // Settle the last change of the slide being left before starting on the new one.
  if (tracker.pendingSlideId !== null && tracker.pendingSlideId !== slideId) tracker.settleActive.flush()
  if (slideId !== tracker.activeSlideId) {
    tracker.activeSlideId = slideId
    tracker.switchedAt = Date.now()
  }
  tracker.pendingSlideId = slideId
  tracker.settleActive(snapshot)
}

const onVisibilityChange = (tracker) => {
  if (!isTabVisible()) return
  if (tracker.latestSnapshot) tracker.settleActive(tracker.latestSnapshot)
  tracker.scanOthers()
}

export default (store) => {
  const tracker = {
    store,
    // Each slide's images as last settled, kept across switches so coming back to a slide is
    // compared with what it had, and an upload finishing after leaving is still seen.
    settledNames: new Map(),
    activeSlideId: null,
    switchedAt: 0,
    pendingSlideId: null,
    latestSnapshot: null,
  }
  tracker.settleActive = debounce((snapshot) => settleActiveSlide(tracker, snapshot), IMAGE_SETTLE_MS)
  tracker.scanOthers = debounce(() => scanOtherSlides(tracker), OTHER_SLIDES_SCAN_MS)

  store.watch(activeSlideSnapshot, (snapshot) => onActiveSlideImages(tracker, snapshot), { immediate: true })
  store.subscribe(() => tracker.scanOthers())
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => onVisibilityChange(tracker))
  }
}
